import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type {
  CancelSyncArgs,
  CancelSyncResponse,
  SyncHistoryArgs,
  SyncHistoryResponse,
  TriggerSyncArgs,
  TriggerSyncResponse,
} from './catalogSyncTypes';

/**
 * Pure request builders — exported so the exact wire (URL + params + body) is
 * lockable in a `toEqual` unit test (`fetchBaseQuery` can't be intercepted, so
 * this is how the request shape is pinned). Endpoints:
 *   GET  /sku/v1/productMatchings/syncCatalog/history?page&pageSize[&status]
 *   POST /sku/v1/productMatchings/syncCatalog                 body { full }
 *   POST /sku/v1/productMatchings/syncCatalog/{syncId}/cancel body {}
 */
export const catalogSyncRequestBuilders = {
  getSyncHistory: ({ page = 1, pageSize = 10, status }: SyncHistoryArgs) => {
    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('pageSize', String(pageSize));
    // Omit an unset status so the standalone request stays byte-identical.
    if (status) qs.set('status', status);
    return {
      url: `${services.sku}/v1/productMatchings/syncCatalog/history?${qs.toString()}`,
      method: 'GET' as const,
    };
  },
  triggerSync: ({ full = false }: TriggerSyncArgs) => ({
    url: `${services.sku}/v1/productMatchings/syncCatalog`,
    method: 'POST' as const,
    body: { full },
  }),
  cancelSync: ({ syncId }: CancelSyncArgs) => ({
    url: `${services.sku}/v1/productMatchings/syncCatalog/${encodeURIComponent(syncId)}/cancel`,
    method: 'POST' as const,
    body: {},
  }),
};

/** Tolerate a defensive `{ data: {...} }` wrapper (other portal services ship both). */
function unwrap<T>(response: T | { data?: T } | unknown): T | undefined {
  const r = response as { data?: T };
  if (r && typeof r === 'object' && 'data' in r && r.data != null) return r.data;
  return response as T;
}

/** Unwrap the sync-history envelope; default to an empty page on an unexpected shape. */
export function unwrapSyncHistory(response: unknown): SyncHistoryResponse {
  const d = unwrap<SyncHistoryResponse>(response);
  const items = Array.isArray(d?.items) ? d!.items : [];
  return {
    items,
    total: d?.total ?? items.length,
    page: d?.page ?? 1,
    pageSize: d?.pageSize ?? 10,
  };
}

/** Unwrap the trigger-accepted envelope. */
export function unwrapTriggerSync(response: unknown): TriggerSyncResponse {
  const d = unwrap<TriggerSyncResponse>(response);
  return {
    triggerId: d?.triggerId ?? '',
    full: d?.full ?? false,
    triggeredBy: d?.triggeredBy ?? '',
    acceptedAt: d?.acceptedAt ?? '',
  };
}

/** Unwrap the cancel-accepted envelope. */
export function unwrapCancelSync(response: unknown): CancelSyncResponse {
  const d = unwrap<CancelSyncResponse>(response);
  return {
    syncId: d?.syncId ?? '',
    requestedBy: d?.requestedBy ?? '',
    requestedAt: d?.requestedAt ?? '',
  };
}

/**
 * Catalog Sync slice (super-admin surface, flag `omProd722AiCatalogMappingAssistant`).
 * Reads the history of catalog-sync runs and triggers/cancels them. Kept as its
 * own slice (separate from `productMatchingApi`) because catalog sync is a
 * distinct subsystem — a different collection and lifecycle from matching runs —
 * so it carries its own cache tag. Trigger and cancel both invalidate the
 * history list so the grid refreshes after either action.
 */
export const catalogSyncApi = createApi({
  reducerPath: 'catalogSyncApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['CatalogSync'],
  endpoints: (builder) => ({
    // GET /sku/v1/productMatchings/syncCatalog/history?page&pageSize[&status]
    getSyncHistory: builder.query<SyncHistoryResponse, SyncHistoryArgs>({
      query: catalogSyncRequestBuilders.getSyncHistory,
      transformResponse: unwrapSyncHistory,
      providesTags: [{ type: 'CatalogSync', id: 'HISTORY' }],
    }),
    // POST /sku/v1/productMatchings/syncCatalog — enqueues a run; the worker
    // inserts the Running record shortly after, so we refresh the list.
    triggerSync: builder.mutation<TriggerSyncResponse, TriggerSyncArgs>({
      query: catalogSyncRequestBuilders.triggerSync,
      transformResponse: unwrapTriggerSync,
      invalidatesTags: [{ type: 'CatalogSync', id: 'HISTORY' }],
    }),
    // POST /sku/v1/productMatchings/syncCatalog/{syncId}/cancel — refreshes the list.
    cancelSync: builder.mutation<CancelSyncResponse, CancelSyncArgs>({
      query: catalogSyncRequestBuilders.cancelSync,
      transformResponse: unwrapCancelSync,
      invalidatesTags: [{ type: 'CatalogSync', id: 'HISTORY' }],
    }),
  }),
});

export const {
  useGetSyncHistoryQuery,
  useLazyGetSyncHistoryQuery,
  useTriggerSyncMutation,
  useCancelSyncMutation,
} = catalogSyncApi;
